import {
  Accordion,
  Badge,
  Box,
  Card,
  Divider,
  Flex,
  Grid,
  Group,
  rem,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { IconAlertCircle, IconCheck, IconInfoCircle } from "@tabler/icons-react";
import React from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  CPTAssessment,
  CPTIndexAssessment,
  CPTIndexKey,
  CPTSeverity,
  INDEX_LABEL_KEY,
  INDEX_RAW_DIRECTION,
  SEVERITY_BADGE_KEY,
  SEVERITY_MANTINE_COLOR,
  TranslatedText,
  VERDICT_DESCRIPTION_KEY,
  VERDICT_HEADLINE_KEY,
  toPerformanceZ,
} from "../utils/CPTAssessmentUtils";
import { CPTBellCurve } from "./CPTBellCurve";
import { normalCdf } from "../../readingAssessment/utils/ReadingRiskUtils";
import "./ContinuousPerformanceDashboard.scss";

interface DashboardProps {
  assessment: CPTAssessment;
}

const INDEX_ORDER: CPTIndexKey[] = [
  "attentiveness",
  "timeliness",
  "impulsiveness",
  "hyperReactivity",
];

function VerdictIcon({ severity }: { severity: CPTSeverity }) {
  if (severity === "inconclusive") return <IconAlertCircle size={32} />;
  if (severity === "expected") return <IconCheck size={32} />;
  return <IconInfoCircle size={32} />;
}

function formatZ(z: number | null): string {
  if (z === null) return "—";
  const sign = z >= 0 ? "+" : "";
  return `${sign}${z.toFixed(2)}`;
}

function formatRate(value: number | null | undefined, digits = 1): string {
  if (value === undefined || value === null || !Number.isFinite(value)) return "—";
  return `${(value * 100).toFixed(digits)}%`;
}

function formatMs(value: number | null | undefined): string {
  if (value === undefined || value === null || !Number.isFinite(value)) return "—";
  return `${Math.round(value)} ms`;
}

function renderTranslated(value: TranslatedText, t: TFunction): string {
  return t(value.key, value.params ?? {});
}

const VerdictHeader: React.FC<DashboardProps> = ({ assessment }) => {
  const { t, i18n } = useTranslation();
  const color = assessment.verdict.color;
  const concernLabels = assessment.verdict.concernIndexKeys.map((k) => t(INDEX_LABEL_KEY[k]));
  const concernSentence = new Intl.ListFormat(i18n.language, {
    style: "long",
    type: "conjunction",
  }).format(concernLabels);
  const headline = t(VERDICT_HEADLINE_KEY[assessment.verdict.severity]);
  const description = t(VERDICT_DESCRIPTION_KEY[assessment.verdict.severity], {
    indices: concernSentence,
  });

  return (
    <Card
      withBorder
      radius="md"
      p="md"
      bg={`${color}.0`}
      style={{
        borderColor: `var(--mantine-color-${color}-3)`,
        borderLeft: `${rem(8)} solid var(--mantine-color-${color}-6)`,
      }}
    >
      <Stack gap="sm">
        <Flex align="center" gap="md">
          <Box c={`${color}.6`}>
            <VerdictIcon severity={assessment.overallSeverity} />
          </Box>
          <Box>
            <Text size="xs" fw={800} tt="uppercase" c={`${color}.7`} lts="0.2em">
              {t("extraTests.continuousPerformance.result.verdict.eyebrow")}
            </Text>
            <Title order={2} c={`${color}.9`} fz={rem(32)} lh={1.1} lts="-0.01em" mt={rem(2)}>
              {headline}
            </Title>
          </Box>
        </Flex>

        <Divider color={`${color}.2`} />

        <Text size="sm" fw={500} c={`${color}.9`} lh={1.5}>
          {description}
        </Text>

        <Group gap="xs">
          {INDEX_ORDER.map((key) => {
            const idx = assessment.indices[key];
            const sevColor = SEVERITY_MANTINE_COLOR[idx.severity];
            return (
              <Badge key={key} size="sm" color={sevColor} radius="sm">
                {t(INDEX_LABEL_KEY[key])}: {t(SEVERITY_BADGE_KEY[idx.severity])}
              </Badge>
            );
          })}
        </Group>
      </Stack>
    </Card>
  );
};

const INDEX_CARD_HEADER_MIN_HEIGHT = rem(56);
const INDEX_CARD_VALUE_MIN_HEIGHT = rem(72);
const INDEX_CARD_META_MIN_HEIGHT = rem(36);
const INDEX_CARD_RATIONALE_MIN_HEIGHT = rem(48);

function bellStatusColor(performanceZ: number): "green" | "amber" | "red" {
  if (performanceZ >= -0.5) return "green";
  if (performanceZ >= -1.5) return "amber";
  return "red";
}

const IndexCard: React.FC<{ index: CPTIndexAssessment }> = ({ index }) => {
  const { t } = useTranslation();
  const sevColor = SEVERITY_MANTINE_COLOR[index.severity];
  const valueColor =
    index.severity === "expected"
      ? "var(--mantine-color-bright)"
      : `var(--mantine-color-${sevColor}-8)`;

  return (
    <Card
      withBorder
      radius="md"
      p="lg"
      shadow="sm"
      display="flex"
      h="100%"
      style={{ flexDirection: "column" }}
    >
      <Stack gap="xs" style={{ flex: 1 }}>
        <Stack gap={6} mih={INDEX_CARD_HEADER_MIN_HEIGHT}>
          <Text fw={800} size="xs" c="dimmed" tt="uppercase" lts={1}>
            {t(index.labelKey)}
          </Text>
          <Badge color={sevColor} size="sm" radius="sm" style={{ alignSelf: "flex-start" }}>
            {t(SEVERITY_BADGE_KEY[index.severity])}
          </Badge>
        </Stack>

        <Box my="xs">
          <Text size={rem(28)} fw={900} c={valueColor} lh={1.05} mih={INDEX_CARD_VALUE_MIN_HEIGHT}>
            {renderTranslated(index.value, t)}
          </Text>
          <Box mih={INDEX_CARD_META_MIN_HEIGHT}>
            <Text size="xs" c="dimmed" fw={600}>
              {t("extraTests.continuousPerformance.result.zScorePrefix")} {formatZ(index.zScore)}
            </Text>
            {index.context ? (
              <Text size="xs" c="dimmed">
                {renderTranslated(index.context, t)}
              </Text>
            ) : null}
          </Box>
        </Box>

        {index.zScore !== null && Number.isFinite(index.zScore)
          ? (() => {
              const direction = INDEX_RAW_DIRECTION[index.key];
              const rawZ = index.zScore;
              const performanceZ = toPerformanceZ(index.key, rawZ) ?? rawZ;
              return (
                <Box my="xs">
                  <CPTBellCurve
                    data={{
                      percentile: Math.round(normalCdf(rawZ) * 100),
                      zScore: rawZ,
                      populationMean: 0,
                      populationSd: 1,
                      direction,
                    }}
                    statusColor={bellStatusColor(performanceZ)}
                    label={t(index.labelKey)}
                  />
                </Box>
              );
            })()
          : null}

        <Divider variant="dashed" my="xs" />

        <Text size="sm" lh={1.5} mih={INDEX_CARD_RATIONALE_MIN_HEIGHT} style={{ flex: 1 }}>
          {t(index.rationaleKey)}
        </Text>
      </Stack>
    </Card>
  );
};

const NumericSummary: React.FC<DashboardProps> = ({ assessment }) => {
  const { t } = useTranslation();
  const raw = assessment.rawMetrics;
  const counts = assessment.counts;
  const ns = "extraTests.continuousPerformance.result.numericSummary";

  const rowsCounts: Array<{ name: string; measured: string; note?: string }> = [
    {
      name: t(`${ns}.counts.totalStimuli`),
      measured: counts.totalStimuli.toLocaleString(),
      note: t(`${ns}.counts.totalStimuliNote`),
    },
    {
      name: t(`${ns}.counts.targets`),
      measured: counts.totalTargets.toLocaleString(),
      note: t(`${ns}.counts.targetsNote`),
    },
    {
      name: t(`${ns}.counts.nonTargets`),
      measured: counts.totalNonTargets.toLocaleString(),
      note: t(`${ns}.counts.nonTargetsNote`),
    },
    {
      name: t(`${ns}.counts.distractors`),
      measured: counts.totalDistractors.toLocaleString(),
      note: t(`${ns}.counts.distractorsNote`),
    },
    {
      name: t(`${ns}.counts.responsesRecorded`),
      measured: counts.totalResponses.toLocaleString(),
      note: t(`${ns}.counts.responsesRecordedNote`),
    },
  ];

  const rowsRaw: Array<{ name: string; measured: string; note?: string }> = raw
    ? [
        {
          name: t(`${ns}.raw.hits`),
          measured: `${raw.hits}`,
          note: t(`${ns}.raw.hitsNote`),
        },
        {
          name: t(`${ns}.raw.hitsOnTime`),
          measured: `${raw.timingCount}`,
          note: t(`${ns}.raw.hitsOnTimeNote`),
        },
        {
          name: t(`${ns}.raw.misses`),
          measured: `${raw.misses}`,
          note: t(`${ns}.raw.missesNote`),
        },
        {
          name: t(`${ns}.raw.falseAlarms`),
          measured: `${raw.falseAlarms}`,
          note: t(`${ns}.raw.falseAlarmsNote`),
        },
        {
          name: t(`${ns}.raw.correctRejections`),
          measured: `${raw.correctRejections}`,
          note: t(`${ns}.raw.correctRejectionsNote`),
        },
        {
          name: t(`${ns}.raw.extraPresses`),
          measured: `${raw.multiPressCount}`,
          note: t(`${ns}.raw.extraPressesNote`),
        },
        {
          name: t(`${ns}.raw.hitRate`),
          measured: formatRate(raw.hitRate),
          note: t(`${ns}.raw.hitRateNote`),
        },
        {
          name: t(`${ns}.raw.onTimeRate`),
          measured: formatRate(raw.timingRate),
          note: t(`${ns}.raw.onTimeRateNote`),
        },
        {
          name: t(`${ns}.raw.falseAlarmRate`),
          measured: formatRate(raw.falseAlarmRate),
          note: t(`${ns}.raw.falseAlarmRateNote`),
        },
        {
          name: t(`${ns}.raw.meanRt`),
          measured: formatMs(raw.meanResponseTimeMs),
          note: t(`${ns}.raw.meanRtNote`),
        },
        {
          name: t(`${ns}.raw.medianRt`),
          measured: formatMs(raw.medianResponseTimeMs),
          note: t(`${ns}.raw.medianRtNote`),
        },
        {
          name: t(`${ns}.raw.rtSd`),
          measured: formatMs(raw.sdResponseTimeMs),
          note: t(`${ns}.raw.rtSdNote`),
        },
      ]
    : [];

  const indexRows = INDEX_ORDER.map((key) => {
    const idx = assessment.indices[key];
    return {
      key,
      name: t(INDEX_LABEL_KEY[key]),
      measured: renderTranslated(idx.value, t),
      zScore: formatZ(idx.zScore),
      severity: idx.severity,
    };
  });

  return (
    <Accordion
      defaultValue="numeric-summary"
      variant="contained"
      radius="md"
      chevronPosition="left"
    >
      <Accordion.Item value="numeric-summary">
        <Accordion.Control>
          <Text size="xs" fw={800} c="dimmed" tt="uppercase" lts={1}>
            {t(`${ns}.title`)}
          </Text>
        </Accordion.Control>
        <Accordion.Panel>
          <Title order={6} c="dimmed" mb="xs">
            {t(`${ns}.indexZScores`)}
          </Title>
          <Table striped withTableBorder withColumnBorders verticalSpacing="sm" mb="lg">
            <Table.Thead>
              <Table.Tr>
                <Table.Th w="28%">{t(`${ns}.headers.index`)}</Table.Th>
                <Table.Th w="20%">{t(`${ns}.headers.measured`)}</Table.Th>
                <Table.Th w="20%">{t(`${ns}.headers.zScore`)}</Table.Th>
                <Table.Th>{t(`${ns}.headers.severity`)}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {indexRows.map((row) => (
                <Table.Tr key={row.key}>
                  <Table.Td>{row.name}</Table.Td>
                  <Table.Td>{row.measured}</Table.Td>
                  <Table.Td>{row.zScore}</Table.Td>
                  <Table.Td>
                    <Badge
                      color={SEVERITY_MANTINE_COLOR[row.severity]}
                      size="sm"
                      radius="sm"
                      w={120}
                      h={20}
                    >
                      {t(SEVERITY_BADGE_KEY[row.severity])}
                    </Badge>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>

          <Table striped withTableBorder withColumnBorders verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th w="28%">{t(`${ns}.headers.measure`)}</Table.Th>
                <Table.Th w="20%">{t(`${ns}.headers.value`)}</Table.Th>
                <Table.Th>{t(`${ns}.headers.definition`)}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              <Table.Tr>
                <Table.Td colSpan={3} bg="gray.0">
                  <Text size="xs" fw={800} c="dimmed" tt="uppercase" lts={1}>
                    {t(`${ns}.stimuliAndResponses`)}
                  </Text>
                </Table.Td>
              </Table.Tr>
              {rowsCounts.map((row) => (
                <Table.Tr key={row.name}>
                  <Table.Td>{row.name}</Table.Td>
                  <Table.Td>{row.measured}</Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {row.note ?? ""}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
              {raw ? (
                <>
                  <Table.Tr>
                    <Table.Td colSpan={3} bg="gray.0">
                      <Text size="xs" fw={800} c="dimmed" tt="uppercase" lts={1}>
                        {t(`${ns}.trialOutcomes`)}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                  {rowsRaw.map((row) => (
                    <Table.Tr key={row.name}>
                      <Table.Td>{row.name}</Table.Td>
                      <Table.Td>{row.measured}</Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">
                          {row.note ?? ""}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </>
              ) : (
                <Table.Tr>
                  <Table.Td colSpan={3}>
                    <Text size="xs" c="dimmed">
                      {t(`${ns}.noTrialMetrics`)}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
};

export const ContinuousPerformanceDashboard: React.FC<DashboardProps> = ({ assessment }) => {
  return (
    <Box className="cpt-dashboard">
      <Stack gap="xl">
        <VerdictHeader assessment={assessment} />
        <Grid gutter="lg" align="stretch">
          {INDEX_ORDER.map((key) => (
            <Grid.Col key={key} span={{ base: 12, sm: 6, md: 3 }} className="cpt-col-3">
              <IndexCard index={assessment.indices[key]} />
            </Grid.Col>
          ))}
        </Grid>
      </Stack>
    </Box>
  );
};

export const ContinuousPerformanceScoreSummary: React.FC<DashboardProps> = ({ assessment }) => {
  return (
    <Box className="cpt-dashboard">
      <NumericSummary assessment={assessment} />
    </Box>
  );
};
